import React from "react";
import { connect } from "react-redux";
import {
  Layout,
  Table,
  Space,
  Tag,
  Tooltip,
  Button,
  Row,
  Col,
  Form,
} from "antd";
import {
  InfoCircleOutlined as InfoCircle,
  FileExcelFilled,
  CloseCircleOutlined,
} from "@ant-design/icons";
import {
  getTransactionFee,
  getUrlTransactionFee
} from "services/report-service";
import { CardInfo, FormatDate, FormatMoney } from "components";
import moment from "moment";
import { formatDate, formatMoney, getMaskedCpf } from "utils";
import lodash from "lodash";
import { writeFile, utils } from "xlsx";
import { DatePicker } from 'antd';
import Search from "antd/es/input/Search";
import TransferTableComponent from "./transfer-table-component";
import { AccountSystem } from "utils/account-system";
import { getTotalBalance } from "services/company-service";
const { RangePicker } = DatePicker;

const { Footer, Content } = Layout;

const mapStateToProps = (state) => ({
  login: state.authReducer.loginReducer,
});

const DataFetch = {
  data: [],
  loading: false,
};

const TransferScreen = (props) => {
  const [data, setData] = React.useState(DataFetch);
  const [dataTable, setDataTable] = React.useState();
  const [dataTableFiltered, setDataTableFiltered] = React.useState();
  const [selectedRowKeys, setSelectedRowKeys] = React.useState([]);
  const [defaultFilter, setDefaultFilter] = React.useState([]);
  const hasSearched = React.useRef(false);
  const [bagAmount, setBagAmount] = React.useState(0);
  const [transferAmount, setTransferAmount] = React.useState(0);
  const [feeAmount, setFeeAmount] = React.useState(0);
  const [quantity, setQuantity] = React.useState(0);
  const [filterQuantity, setFilterQuantity] = React.useState(0);
  const [filteredTransfer, setFilteredTransfer] = React.useState([]);
  const [filterTotalValue, setFilterTotalValue] = React.useState(0);
  const [filterTotalCreditValue, setFilterTotalCreditValue] = React.useState(0);
  const [filterTotalFundsInValue, setFilterTotalFundsInValue] = React.useState(0);
  const [filterFeeAmount, setFilterFeeAmount] = React.useState(0);
  const [system, setSystem] = React.useState(AccountSystem.SWAP);
  const [loading, setLoading] = React.useState(false);
  const [url, setUrl] = React.useState('');
  const [form] = Form.useForm();
  const filterTypes = {
    credit: 'Administrativa',
    charge: 'Individual',
    fundsIn: 'FundsIn',
    fundsInCorporate: 'FundsInCorporate'
  }

  React.useEffect(() => {
    if (!hasSearched.current) {
      loadUrl();
      hasSearched.current = true;
    }
  }, []);

  React.useEffect(() => {
    if (url) {
      loadBalance();
      load();
    }
  }, [url]);

  React.useEffect(() => {
    if(defaultFilter && defaultFilter.length > 0) {
      const values = filteredTransfer || data.data;
      const lowercasedFilters = defaultFilter.map(filter => filter.toLowerCase());
      const filteredValues = values.filter(userData => {
        return lowercasedFilters.some(filter =>
          userData.type.toLowerCase() === filter ||
          userData.company.toLowerCase().includes(filter)
        );
      });
      setSelectedRowKeys(prev => prev.filter((row) => filteredValues.some(transfer => transfer.id === row)));

      setFilteredValues(filteredValues);
    } else if(filteredTransfer) {
      setFilteredValues(filteredTransfer);
      setSelectedRowKeys([]);
    }
  }, [defaultFilter, filteredTransfer]);

  React.useEffect(() => {
    if (data.data.length > 0 || filteredTransfer.length > 0) {
      setDefaultFilter([]);
    }
  }, [data]);

  const loadUrl = async () => {
    try{
      setLoading(true);
      const resUrls = await getUrlTransactionFee();
      setUrl(resUrls.urls.reportTransactionFee);
    } finally {
      setLoading(false);
    }
  };

  const loadBalance = async () => {
    try {
      setLoading(true);
      const balance = await getTotalBalance(null, system);
      setBagAmount(balance ? balance.balance : 0);
    } finally {
      setLoading(false);
    }
  }

  const load = async (date = undefined) => {
    try {
      setLoading(true);
      let res = await getTransactionFee(url, date || getCurrentMonth());
      let processed = processTransactionFee(res);
      setFilterQuantity(processed.qtd);
      setQuantity(processed.qtd);
      setData({ data: processed.values });
      setDataTable(res);
      setDataTableFiltered(res);
      setFilteredTransfer(sortTable(processed.values));
      setTransferAmount(processed.amount);
      setFeeAmount(processed.fee);
      recalculateTotalCreditValue(processed.values);
      recalculateTotalFundsInValue(processed.values);
      recalculateTotalValue(processed.values);
      setFilterFeeAmount(processed.fee);
    } finally {
      setLoading(false);
    }
  };

  const processTransactionFee = (data) => {
    const keys = Object.keys(data);
    let [amount, fee, qtd] = [0, 0, 0];
    const grouped = {};
    for (let key of keys) {
      amount += data[key].value;
      fee += data[key].fee;
      for (let value of data[key].results) {
        qtd += Number(value.qtd);
        const month = getMonth(value.date);
        const groupKey = [month, value.company, value.type, value.filter].join('|');
        if (!grouped[groupKey]) {
          grouped[groupKey] = { ...value, month, date: formatMonth(month), value: 0, fee: 0, qtd: 0 };
        }
        const group = grouped[groupKey];
        group.value = roundMoney(group.value + value.value);
        group.fee = roundMoney(group.fee + value.fee);
        group.qtd += Number(value.qtd);
      }
    }
    const values = Object.values(grouped);
    values.forEach((value, index) => { value.id = index + 1; });
    return {
      amount, fee, qtd, count: values.length, values
    }
  }

  const roundMoney = (value) => Math.round(value * 100) / 100;

  const getMonth = (dateString) => {
    if (dateString.includes('/')) {
      const [, month, year] = dateString.split('/');
      return `${year}-${month}`;
    }
    return dateString.substring(0, 7);
  };

  const formatMonth = (month) => {
    const [year, monthNumber] = month.split('-');
    return `${monthNumber}/${year}`;
  };

  const setFilteredValues = (data) => {
    const [totalFee, totalQuantity] = data.reduce(
      ([feeSum, quantity], currentValue) => [
        feeSum + currentValue.fee,
        quantity + currentValue.qtd
      ],
      [0, 0]
    );

    recalculateTotalValue(data);
    recalculateTotalCreditValue(data);
    recalculateTotalFundsInValue(data);
    setFilterFeeAmount(totalFee);
    setFilterQuantity(totalQuantity);
  }

  const recalculateTotalValue = (data) => {
    const [valueFiltered] = data.filter(item => item.filter !== filterTypes.credit && item.filter !== filterTypes.charge
      && item.filter !== filterTypes.fundsIn && item.filter !== filterTypes.fundsInCorporate).reduce(([valueSumFiltered], currentValue) => [
      valueSumFiltered + currentValue.value
    ],
    [0]);
    setFilterTotalValue(valueFiltered);
  }

  function recalculateTotalCreditValue(data) {
    const [valueFiltered] = data.filter(item => item.filter === filterTypes.credit).reduce(([valueSumFiltered], currentValue) => [
      valueSumFiltered + currentValue.value
    ],
    [0]);
    setFilterTotalCreditValue(valueFiltered);
  }

  function recalculateTotalFundsInValue(data) {
    const [valueFiltered] = data.filter(item => item.filter === filterTypes.fundsIn || item.filter === filterTypes.fundsInCorporate).reduce(([valueSumFiltered], currentValue) => [
      valueSumFiltered + currentValue.value
    ],
    [0]);
    setFilterTotalFundsInValue(valueFiltered);
  }

  const onExport = () => {
    try {
      if (selectedRowKeys.length === 0) {
        return;
      }

      setLoading(true);

      const selectedData = filteredTransfer.filter((row) =>
        selectedRowKeys.includes(row.id)
      );

      const usersTransfer = selectedData.map(row => {
        return {
          Nome: row.company,
          "Data": `${row.date}`,
          "Quantidade": row.qtd,
          "Valor Transacionado": formatMoney(row.value, false),
          Tarifa: formatMoney(row.fee, false),
          "Tipo de taxa": row.type,
        }
      });

      const workSheet = utils.json_to_sheet(usersTransfer);
      const workBook = utils.book_new();
      utils.book_append_sheet(workBook, workSheet, 'Transferências');
      const fileName = `relatorio_${new Date().toISOString()}.xlsx`;
      writeFile(workBook, fileName);
    } finally {
      setLoading(false);
    }
  }

  const onFilter = (searchValue) => {
    try {
      setLoading(true);

      if (!searchValue || searchValue === '') {
        setFilteredTransfer(data.data);
        recalculateTotalValue(data.data);
        recalculateTotalCreditValue(data.data);
        recalculateTotalFundsInValue(data.data);
        setFilterFeeAmount(feeAmount);
        setFilterQuantity(quantity);
        setDataTableFiltered(dataTable);
        setLoading(false);
        return;
      }

      const values = data.data;
      const lowercasedValue = searchValue.toLowerCase();
      const filteredValues = values.filter((userData) => {
        return (
          userData.company.toLowerCase().includes(lowercasedValue)
        );
      });

      setFilteredTransfer(filteredValues);

      const [totalFee, totalQuantity] = filteredValues.reduce(
        ([feeSum, quantity], currentValue) => [
          feeSum + currentValue.fee,
          quantity + currentValue.qtd
        ],
        [0, 0]
      );

      recalculateTotalValue(filteredValues);
      recalculateTotalCreditValue(filteredValues);
      recalculateTotalFundsInValue(filteredValues);
      setFilterFeeAmount(totalFee);
      setFilterQuantity(totalQuantity);


      let dataValues = recalculateByCompany(dataTable, searchValue);
      setDataTableFiltered(dataValues);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (dates) => {
    if(!dates || (dates[0].length === 0 && dates[1].length === 0)) {
      load();
    } else {
      const [startDate, endDate] = dates.map(date => {
        const [day, month, year] = date.split('/');
        return `${year}-${month}-${day}`;
      });

      load(`${startDate}:${endDate}`);
    }
  }

  function recalculateByCompany(data, companyName) {
    const updatedData = {};

    Object.keys(data).forEach(key => {
      const category = data[key];
      const lowercasedCompany = companyName.toLowerCase();
      const filteredResults = category.results.filter(
        item => item.company.toLowerCase().includes(lowercasedCompany)
      );

      const totalFee = filteredResults.reduce((sum, item) => sum + item.fee, 0);
      const totalQtd = filteredResults.reduce((sum, item) => sum + item.qtd, 0);
      const totalValue = filteredResults.reduce((sum, item) => sum + item.value, 0);

      updatedData[key] = {
        ...category,
        results: filteredResults,
        fee: totalFee,
        value: totalValue,
        qtd: totalQtd
      };
    });

    return updatedData;
  }

  const sortTable = (data) => {
    return data.sort((a, b) => {
        if (a.month < b.month) return -1;
        if (a.month > b.month) return 1;

        if (a.company < b.company) return -1;
        if (a.company > b.company) return 1;

        if (a.type < b.type) return -1;
        if (a.type > b.type) return 1;

        return 0;
    });
  }

  function getCurrentMonth() {
    const today = new Date();

    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const firstDate = `${year}-${month}-01`;

    const currentDay = String(today.getDate()).padStart(2, '0');
    const currentDate = `${year}-${month}-${currentDay}`;

    return `${firstDate}:${currentDate}`;
  }

  return (
    <>
      <Content>
        <Row gutter={12} align="top">
            <Col xl={18}>
                <Row gutter={[12, 12]}>
                    <Col span={8}>
                        <CardInfo loading={loading} title="Saldo Atual" value={formatMoney(bagAmount)} />
                    </Col>
                    <Col span={8}>
                        <CardInfo loading={loading} title="Valor Total Transacionado em Crédito" value={formatMoney(filterTotalCreditValue)} />
                    </Col>
                    <Col span={8}>
                        <CardInfo loading={loading} title="Valor Total Funds In" value={formatMoney(filterTotalFundsInValue)} />
                    </Col>
                    <Col span={8}>
                        <CardInfo loading={loading} title="Valor Total Transacionado" value={formatMoney(filterTotalValue)} />
                    </Col>
                    <Col span={8}>
                        <CardInfo loading={loading} title="Valor Total de Tarifas" value={formatMoney(filterFeeAmount)} />
                    </Col>
                    <Col span={8}>
                        <CardInfo loading={loading} title="Quantidade" value={filterQuantity} />
                    </Col>
                </Row>
            </Col>
            <Col xl={6}>
                <TransferTableComponent
                    data={dataTableFiltered}
                />
            </Col>
        </Row>

        <Row justify="space-between" style={{marginTop: '20px', padding: '0'}}>
          <Col xl={6}>
            <Form
              name="basic"
              form={form}
              onFinish={(values) => {
                onFilter(values.filterValue);
              }}
            >
              <Form.Item
                name="filterValue"
              >
                <Search
                  onSearch={onFilter}
                  placeholder="Empresa"
                  allowClear={false}
                  disabled={loading}
                  suffix={
                    !loading && (<Tooltip title="Limpar Pesquisa">
                      <CloseCircleOutlined
                        style={{ cursor: 'pointer'}}
                        onClick={() => {
                          form.resetFields();
                          onFilter('');
                          setDefaultFilter([]);
                          handleDateChange(null);
                        }}
                      />
                    </Tooltip>)
                  }
                />
              </Form.Item>
            </Form>
          </Col>
          <Col>
            <RangePicker
                format="DD/MM/YYYY"
                inputReadOnly={false}
                allowClear={true}
                disabled={loading}
                showNow={true}
                onChange={(_, dates) => {
                  form.resetFields();
                  setDefaultFilter([]);
                  handleDateChange(dates);
                }}
                clearIcon={
                  !loading && (<Tooltip title="Limpar Datas">
                    <CloseCircleOutlined style={{ cursor: 'pointer'}} />
                  </Tooltip>)
                }
                disabledDate={(current) => {
                  return current && current > moment().endOf('day');
                }}
            />
          </Col>
        </Row>
        <Table
          rowKey={"id"}
          tableLayout="fixed"
          scroll={{ y: "50vh" }}
          dataSource={filteredTransfer}
          loading={loading}
          pagination={false}
          bordered
          rowSelection={{
            selectedRowKeys: selectedRowKeys,
            onChange: setSelectedRowKeys,
            hideSelectAll: false,
          }}
          onChange={(pagination, filters, sorter) =>
            setDefaultFilter(prev =>
              filters.type && filters.type.length > 0 ? filters.type : []
            )
          }
        >

          <Table.Column
            title="Nome"
            align={"center"}
            dataIndex="company"
            key="company"
            render={value => value}
          />
          <Table.Column
            align={"center"}
            title="Mês"
            dataIndex="date"
            key="date"
            render={value => value}
          />
          <Table.Column
            align={"center"}
            title="Quantidade"
            dataIndex="qtd"
            key="qtd"
            render={value => value}
          />
          <Table.Column
            align={"center"}
            title="Valor Transacionado"
            dataIndex="value"
            key="value"
            render={value => <FormatMoney value={value} />}
          />
          <Table.Column
            align={"center"}
            title="Tarifas"
            dataIndex="fee"
            key="fee"
            render={value => <FormatMoney value={value} />}
          />
          <Table.Column
            align="center"
            title="Tipo de taxa"
            dataIndex="type"
            key="type"
            filters={lodash.uniqWith(
              data.data.length > 0
                ? data.data
                .filter(d => d.type)
                .sort((a, b) => String(a.type).localeCompare(b.type))
                .map(d => {
                  return { text: d.type, value: d.type }
                  })
                : [],
              lodash.isEqual
            )}
            onFilter={(value, record) => record.type === value}
            filteredValue={defaultFilter}
            filterMultiple={true}
            render={value => <>{value}</>}
          />
        </Table>
      </Content>
      {data.data.length > 0 && (
        <Footer>
          <Row justify="end" style={{ width: "100%" }}>
            <Col>
              <Button
                loading={loading}
                disabled={selectedRowKeys.length === 0}
                type="primary"
                icon={<FileExcelFilled />}
                onClick={onExport}
              >
                Exportar Relatório
              </Button>
            </Col>
          </Row>
        </Footer>
      )}
    </>
  );
};

export default connect(mapStateToProps, {})(TransferScreen);
